/* PALISADE CUSTOM — floating pill header behaviors.
 *
 * Ports the patterns Horizon splits across header.js (sticky state + header
 * height custom properties) and header-drawer.js (animated drawer toggle with
 * focus trap). The stock theme.liquid inline script and utilities.js only
 * maintain --header-height / --header-group-height for `header-component`,
 * which this section replaces, so the same properties are maintained here.
 */
import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';
import { getScrollTop, getScrollEventTarget, scrollContainerMediaQuery } from '@theme/scroll-container';

/**
 * Palisade site header: scroll state, height custom properties and the
 * mobile menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} [drawerDetails] - The mobile drawer `<details>` element.
 * @property {HTMLElement} [drawerSummary] - The drawer toggle `<summary>` element.
 * @property {HTMLElement} [drawerPanel] - The slideable drawer panel.
 *
 * @extends {Component<Refs>}
 */
class PalisadeHeaderComponent extends Component {
  #lastScrollY = 0;

  #scrollRaf = 0;

  /**
   * Header height cached by #updateHeightProperties (kept fresh by the
   * ResizeObserver) so the per-frame scroll handler never reads offsetHeight —
   * a layout read right after the dataset write above it would force a
   * synchronous reflow on every scroll frame.
   */
  #height = 0;

  /**
   * The element the scroll listener is bound to. Since the v4.1.1 squeeze
   * layout, the page scrolls inside `.page-wrapper` on desktop (≥990px) and on
   * `document` on mobile — NOT `window` — so we bind to getScrollEventTarget()
   * and rebind when the breakpoint is crossed. Listening on `window` left
   * data-scrolled stuck at 'false' on desktop (window never scrolls), so the
   * transparent header never went opaque. @type {EventTarget | null}
   */
  #scrollTarget = null;

  /**
   * Whether the last drawer toggle came from a pointer.
   *
   * Closing returns focus to the summary, which is right for keyboard users,
   * but Chrome treats a programmatic focus() as focus-visible — so after a tap
   * the label kept the coral + underline treatment while the drawer was already
   * closed and reading "Menu". Focus still returns either way; only the visible
   * ring is suppressed when the interaction was a pointer.
   */
  #pointerToggled = false;

  #resizeObserver = new ResizeObserver(() => this.#updateHeightProperties());

  connectedCallback() {
    super.connectedCallback();

    this.#updateHeightProperties();
    this.#updateScrollState();
    this.#scrollTarget = getScrollEventTarget();
    this.#scrollTarget.addEventListener('scroll', this.#onScroll, { passive: true });
    scrollContainerMediaQuery.addEventListener('change', this.#onScrollContainerChange);
    this.addEventListener('keyup', this.#onKeyUp);
    this.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#scrollTarget?.removeEventListener('scroll', this.#onScroll);
    scrollContainerMediaQuery.removeEventListener('change', this.#onScrollContainerChange);
    if (this.#scrollRaf) cancelAnimationFrame(this.#scrollRaf);
    this.removeEventListener('keyup', this.#onKeyUp);
    this.#resizeObserver.disconnect();
    document.documentElement.removeAttribute('scroll-lock');
  }

  /**
   * Rebinds the scroll listener when the viewport crosses the 990px squeeze
   * breakpoint, where the scroll container switches between `.page-wrapper`
   * (desktop) and `document` (mobile).
   */
  #onScrollContainerChange = () => {
    this.#scrollTarget?.removeEventListener('scroll', this.#onScroll);
    this.#scrollTarget = getScrollEventTarget();
    this.#scrollTarget.addEventListener('scroll', this.#onScroll, { passive: true });
    this.#updateScrollState();
  };

  /**
   * rAF-throttled scroll listener so scroll-state updates run at most once per
   * frame instead of on every scroll event (layout reads live in the
   * ResizeObserver path, not here — see #height).
   */
  #onScroll = () => {
    if (this.#scrollRaf) return;
    this.#scrollRaf = requestAnimationFrame(() => {
      this.#scrollRaf = 0;
      this.#updateScrollState();
    });
  };

  /** @returns {boolean} Whether the mobile menu drawer is open */
  get isDrawerOpen() {
    return this.refs.drawerDetails?.hasAttribute('open') ?? false;
  }

  /**
   * Toggles the mobile menu drawer. Bound via `on:click="/toggleDrawer"` on
   * the drawer `<summary>`.
   * @param {Event} [event]
   */
  toggleDrawer(event) {
    // Prevent the native <summary> toggle so opening and closing both animate.
    event?.preventDefault();

    // Activating a <summary> from the keyboard reports detail === 0; a real
    // pointer click reports 1 or more.
    this.#pointerToggled = Boolean(event) && event.detail > 0;

    if (this.isDrawerOpen) {
      this.closeDrawer();
    } else {
      this.openDrawer();
    }
  }

  /** Opens the mobile menu drawer */
  openDrawer() {
    const { drawerDetails, drawerSummary } = this.refs;
    if (!drawerDetails || this.isDrawerOpen) return;

    this.#updateDrawerOffset();
    drawerDetails.setAttribute('open', '');
    drawerSummary?.setAttribute('aria-expanded', 'true');
    document.documentElement.setAttribute('scroll-lock', '');

    requestAnimationFrame(() => {
      drawerDetails.classList.add('menu-open');

      const panel = this.refs.drawerPanel ?? drawerDetails;
      onAnimationEnd(panel, () => trapFocus(drawerDetails), { subtree: false });
    });
  }

  /** Closes the mobile menu drawer */
  closeDrawer = () => {
    const { drawerDetails, drawerSummary, drawerPanel } = this.refs;
    if (!drawerDetails || !this.isDrawerOpen) return;

    drawerSummary?.setAttribute('aria-expanded', 'false');
    drawerDetails.classList.remove('menu-open');

    onAnimationEnd(
      drawerPanel ?? drawerDetails,
      () => {
        drawerDetails.removeAttribute('open');
        document.documentElement.removeAttribute('scroll-lock');
        removeTrapFocus();

        // Focus always returns to the toggle — that part is the accessibility
        // requirement and is not conditional. Only the visible treatment is:
        // after a tap there is no ring to restore, so the label would otherwise
        // sit there coral and underlined while already reading "Menu". The
        // attribute is scoped to exactly this programmatic focus and cleared the
        // moment focus leaves, so a later Tab to the toggle still shows normally.
        // (focus({ focusVisible: false }) is only a hint — Chrome declines it in
        // both directions here, which would have hidden the ring from keyboard
        // users too, so the state is carried in CSS instead.)
        const summary = this.refs.drawerSummary;

        if (summary) {
          if (this.#pointerToggled) {
            summary.setAttribute('data-suppress-focus-ring', '');
            summary.addEventListener('blur', () => summary.removeAttribute('data-suppress-focus-ring'), {
              once: true,
            });
          } else {
            // Clear it explicitly: focus stays on the toggle across an
            // open/close cycle, so the blur cleanup above never fires and a
            // pointer close followed by a keyboard one would otherwise leave
            // the attribute set and swallow the keyboard indicator.
            summary.removeAttribute('data-suppress-focus-ring');
          }

          summary.focus({ preventScroll: true });
        }
      },
      { subtree: false }
    );
  };

  /**
   * Closes the drawer when the Escape key is pressed.
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;
    this.#pointerToggled = false;
    this.closeDrawer();
  };

  /** Maintains data-scrolled / data-hidden states from the scroll position */
  #updateScrollState = () => {
    const y = getScrollTop();
    // Only mutate attributes on actual state changes — redundant writes still
    // invalidate style on every scroll frame.
    const scrolled = y > 24 ? 'true' : 'false';
    if (this.dataset.scrolled !== scrolled) this.dataset.scrolled = scrolled;

    if (this.getAttribute('sticky') === 'scroll-up') {
      const delta = y - this.#lastScrollY;

      if (Math.abs(delta) > 2) {
        const shouldHide = delta > 0 && y > this.#height * 2 && !this.isDrawerOpen;
        const hidden = shouldHide ? 'true' : 'false';
        if (this.dataset.hidden !== hidden) this.dataset.hidden = hidden;
      }
    }

    this.#lastScrollY = y;
  };

  /** Pins the drawer panel to the bottom edge of the header bar */
  #updateDrawerOffset() {
    const bottom = Math.max(this.getBoundingClientRect().bottom, 0);
    this.style.setProperty('--palisade-drawer-top', `${Math.round(bottom)}px`);
  }

  /**
   * Mirrors updateHeaderHeights() / updateTransparentHeaderOffset() in
   * utilities.js for consumers of --header-height and --header-group-height
   * (cart drawer, account popover, sticky content offsets).
   */
  #updateHeightProperties() {
    const height = this.offsetHeight;
    this.#height = height;
    document.body.style.setProperty('--header-height', `${height}px`);

    const headerGroup = document.getElementById('header-group');
    let groupHeight = 0;

    if (headerGroup) {
      for (const child of headerGroup.children) {
        if (child instanceof HTMLElement) groupHeight += child.offsetHeight;
      }
    }

    document.body.style.setProperty('--header-group-height', `${Math.max(groupHeight, height)}px`);

    // This header overlays content with a negative margin instead of
    // Horizon's offset variable — keep the boolean at 0 so the first main
    // section is not double-offset by stock styles.
    document.body.style.setProperty('--transparent-header-offset-boolean', '0');

    if (this.isDrawerOpen) this.#updateDrawerOffset();
  }
}

if (!customElements.get('palisade-header-component')) {
  customElements.define('palisade-header-component', PalisadeHeaderComponent);
}
